import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FlightRouteContent } from './flight-route.model';

/** Public origin + base path the app is served under (nginx: /flights/). */
export const SITE_URL = 'https://buupass.com/flights/';
const OG_IMAGE = 'https://buupass.com/buupass.png';
const JSON_LD_ID = 'seo-jsonld';

export interface PageSeo {
  title: string;
  description: string;
  /** Path relative to SITE_URL, e.g. '' or 'nairobi-to-mombasa'. Omit on noindex pages. */
  path?: string;
  noindex?: boolean;
  /** Absolute URL of the social share image; defaults to the BuuPass logo. */
  image?: string;
  jsonLd?: object[];
}

/** Transactional pages: crawlable links, but kept out of the index. */
export const NOINDEX: PageSeo = {
  title: 'BuuPass Flights',
  description: 'Book cheap flights in Kenya and across Africa on BuuPass.',
  noindex: true
};

export const HOME_SEO: PageSeo = {
  title: 'Cheap Flights in Kenya | Book Flight Tickets Online | BuuPass',
  description: 'Compare and book cheap flights from Nairobi to Mombasa, Kisumu, Eldoret, Diani, Malindi, Kampala, Dubai and more. Kenya Airways, Jambojet, Safarilink and others. Pay with M-Pesa.',
  path: '',
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'TravelAgency',
      name: 'BuuPass',
      url: SITE_URL,
      logo: OG_IMAGE,
      areaServed: ['Kenya', 'Uganda', 'Tanzania', 'Rwanda'],
      sameAs: ['https://www.facebook.com/BuuPass/', 'https://twitter.com/buupass', 'https://www.instagram.com/buupass/']
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'BuuPass Flights',
      url: SITE_URL
    }
  ]
};

/** Keep in sync with routeHead() in scripts/seo-prerender.mjs. */
export function flightRouteSeo(route: FlightRouteContent): PageSeo {
  const url = SITE_URL + route.slug;
  return {
    title: route.title,
    description: route.description,
    path: route.slug,
    image: route.image ? SITE_URL + route.image : undefined,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Flights', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: `${route.from.city} to ${route.to.city} flights`, item: url }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: route.faq.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a }
        }))
      }
    ]
  };
}

/**
 * Applies title, description, robots, canonical, Open Graph and JSON-LD from
 * each route's `data.seo` after every navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);

  watchRouter(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.apply(this.deepestSeo(this.router.routerState.snapshot.root) ?? NOINDEX));
  }

  apply(seo: PageSeo): void {
    const url = seo.path !== undefined ? SITE_URL + seo.path : null;

    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ name: 'robots', content: seo.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large' });
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: seo.image ?? OG_IMAGE });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });

    if (url) {
      this.meta.updateTag({ property: 'og:url', content: url });
    } else {
      this.meta.removeTag("property='og:url'");
    }
    this.setCanonical(url);
    this.setJsonLd(seo.noindex ? [] : seo.jsonLd ?? []);
  }

  private deepestSeo(snapshot: ActivatedRouteSnapshot): PageSeo | undefined {
    let seo: PageSeo | undefined;
    for (let node: ActivatedRouteSnapshot | null = snapshot; node; node = node.firstChild) {
      const flightRoute = node.data['flightRoute'] as FlightRouteContent | undefined;
      seo = (node.data['seo'] as PageSeo | undefined) ?? (flightRoute ? flightRouteSeo(flightRoute) : seo);
    }
    return seo;
  }

  private setCanonical(url: string | null): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!url) {
      link?.remove();
      return;
    }
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private setJsonLd(blocks: object[]): void {
    this.document.getElementById(JSON_LD_ID)?.remove();
    if (!blocks.length) {
      return;
    }
    const script = this.document.createElement('script');
    script.id = JSON_LD_ID;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
    this.document.head.appendChild(script);
  }
}
